/**
 * The input layer's brain, with no DOM in it: key, button and wheel codes go in as they happen,
 * and once per sim tick `step` turns them - with the pointer's ground point and the gamepad -
 * into one `Command` (06 §10). `Input` (input.ts) is the thin shell that listens to the browser.
 *
 * Everything handed to the sim is quantised, so a recorded command stream stays compact and
 * replays exactly.
 */

import type { Command, WeaponId } from '../sim/types';
import type { ActionId, Bindings } from './bindings';
import { type Axis, ClassicAim, CLASSIC_AIM_ASSIST, eightWay } from './classicAim';
import { type CoreFrame, DEFAULT_INPUT_OPTIONS, type InputOptions, pointerAim } from './coreFrame';
import type { PadState } from './gamepadMap';
import { GaitLatches } from './gaits';
import { HoldTrigger, ThrowRange } from './latch';
import { PadReader } from './padFrame';
import { QuickKnife } from './quickKnife';
import { cycleWeapon } from './weaponCycle';

export { DEFAULT_INPUT_OPTIONS } from './coreFrame';
export type { CoreFrame, InputOptions } from './coreFrame';

/** Recall must be held 0.6 s so it is never pressed by accident (02 §13). */
export const RECALL_HOLD_TICKS = 36;
const AIM_STEPS = 1024;
const AIM_STEP = (Math.PI * 2) / AIM_STEPS;
/** The pointer this close to the ranger gives no usable direction: the last aim stands. */
const AIM_MIN_DIST = 0.4;

const SELECTS: Partial<Record<ActionId, WeaponId>> = {
  weapon1: 'carbine', weapon2: 'grenade', weapon3: 'law', weapon4: 'bomb', weapon5: 'knife',
  pistol: 'pistol', smoke: 'smoke',
};
const FUSES: Partial<Record<ActionId, 5 | 10 | 15>> = { fuse5: 5, fuse10: 10, fuse15: 15 };

const quarter = (v: number): number => Math.round(v * 4) / 4;
const sixtyFourth = (v: number): number => Math.round(v * 64) / 64;

export class InputCore {
  private options: InputOptions = { ...DEFAULT_INPUT_OPTIONS };
  private readonly down = new Set<string>();
  private readonly gaits = new GaitLatches();
  private readonly recall = new HoldTrigger(RECALL_HOLD_TICKS);
  private readonly knife = new QuickKnife();
  private readonly classic = new ClassicAim();
  private readonly throwRange = new ThrowRange();
  private readonly pad = new PadReader();

  private runPressed = false;
  private crawlPressed = false;
  private sneakPressed = false;
  private knifeTapped = false;
  private pausePressed = false;
  private pendingSelect: WeaponId | null = null;
  private cycle = 0;
  private fuse: 5 | 10 | 15 = 10;
  private lastAim = Math.PI;
  private lastAimDist = 10;
  /** Whichever device was touched last drives the aim. */
  private aimSource: 'pointer' | 'pad' = 'pointer';
  private mapDown = false;
  private airborne = false;

  constructor(private readonly bindings: Bindings) {}

  setOptions(options: Partial<InputOptions>): void {
    if (options.runToggle !== undefined) this.options.runToggle = options.runToggle;
    if (options.crawlToggle !== undefined) this.options.crawlToggle = options.crawlToggle;
    if (options.sneakToggle !== undefined) this.options.sneakToggle = options.sneakToggle;
    if (options.grenadeRange !== undefined) this.options.grenadeRange = options.grenadeRange;
  }

  /**
   * In the aircraft or under the canopy the stick steers, it does not walk: the classic scheme's
   * tap-to-turn and turn-before-you-step would only make the parafoil late. Modern is unaffected.
   */
  setAirborne(airborne: boolean): void {
    this.airborne = airborne;
  }

  /** A key or mouse button went down. Returns its action, so the shell can swallow bound keys. */
  keyDown(code: string): ActionId | null {
    const action = this.bindings.lookup(code);
    if (action === null) return null;
    this.down.add(code);
    this.aimSource = 'pointer';
    this.pressed(action);
    return action;
  }

  keyUp(code: string): void {
    this.down.delete(code);
  }

  /** A press with no release to wait for: a wheel notch. */
  tap(code: string): ActionId | null {
    const action = this.bindings.lookup(code);
    if (action !== null) this.pressed(action);
    return action;
  }

  /** The mouse moved: the pointer has the aim again. */
  pointerUsed(): void {
    this.aimSource = 'pointer';
  }

  /** Focus lost: nothing is held any more. Toggles stand. */
  releaseAll(): void {
    this.down.clear();
  }

  /** True once per press of Pause, on the keyboard or the pad's Start. Ask every frame. */
  consumePause(pad: PadState | null = null): boolean {
    const pressed = this.pad.pausePressed(pad) || this.pausePressed;
    this.pausePressed = false;
    return pressed;
  }

  /** The crawl toggle is input-side state; a downed ranger's forced crawl is the sim's. */
  get crawling(): boolean {
    return this.gaits.crawling;
  }

  /** The map / tactical-view control, keyboard or pad, as of the last `step`. */
  get mapHeld(): boolean {
    return this.mapDown;
  }

  private pressed(action: ActionId): void {
    const weapon = SELECTS[action];
    if (weapon !== undefined) this.pendingSelect = weapon;
    const fuse = FUSES[action];
    if (fuse !== undefined) {
      this.fuse = fuse;
      // 1987: the fuse keys ARE the bomb keys (01 §9).
      if (this.bindings.scheme() === 'classic') this.pendingSelect = 'bomb';
    }
    if (action === 'run') this.runPressed = true;
    else if (action === 'crawl') this.crawlPressed = true;
    else if (action === 'sneak') this.sneakPressed = true;
    else if (action === 'quickKnife') this.knifeTapped = true;
    else if (action === 'pause') this.pausePressed = true;
    else if (action === 'nextWeapon') this.cycle++;
    else if (action === 'prevWeapon') this.cycle--;
  }

  private held(action: ActionId): boolean {
    for (const code of this.bindings.keysFor(action)) if (this.down.has(code)) return true;
    return false;
  }

  /** One sim tick's command. */
  step(frame: CoreFrame): Command {
    const reach = this.options.grenadeRange;
    const pad = this.pad.read(frame.pad, reach);
    if (pad.active) this.aimSource = 'pad';
    const classic = this.bindings.scheme() === 'classic';
    const axis = (plus: ActionId, minus: ActionId): Axis => {
      const v = (this.held(plus) ? 1 : 0) - (this.held(minus) ? 1 : 0);
      return v > 0 ? 1 : v < 0 ? -1 : 0;
    };
    const keyX = axis('moveRight', 'moveLeft');
    const keyZ = axis('moveDown', 'moveUp');
    const keysMove = keyX !== 0 || keyZ !== 0;
    const use = this.held('use') || pad.use;

    let moveX: number;
    let moveZ: number;
    let aimDist: number;
    if (classic) {
      const stick = keysMove ? { dx: keyX, dz: keyZ } : eightWay(pad.moveX, pad.moveZ);
      const out = this.airborne ? null : this.classic.update(stick.dx, stick.dz);
      moveX = out === null ? stick.dx : out.moveX;
      moveZ = out === null ? stick.dz : out.moveZ;
      this.lastAim = out === null ? this.classic.aim : out.aim;
      aimDist = this.throwRange.update(use, reach);
    } else {
      moveX = keysMove ? keyX : sixtyFourth(pad.moveX);
      moveZ = keysMove ? keyZ : sixtyFourth(pad.moveZ);
      if (this.aimSource === 'pad') {
        // A centred stick keeps the last aim: he does not snap round when the thumb lifts.
        if (pad.aim !== null && pad.aimDist !== null) {
          this.lastAim = Math.round(pad.aim / AIM_STEP) * AIM_STEP;
          this.lastAimDist = quarter(pad.aimDist);
        }
      } else if (frame.pointer !== null) {
        // ROUND7 §2: on a man, his feet - not the ground under the cursor, which is behind him.
        const at = pointerAim(frame) ?? frame.pointer;
        const dx = at.x - frame.rangerX;
        const dz = at.z - frame.rangerZ;
        const dist = Math.hypot(dx, dz);
        if (dist > AIM_MIN_DIST) this.lastAim = Math.round(Math.atan2(dx, dz) / AIM_STEP) * AIM_STEP;
        this.lastAimDist = quarter(dist);
      }
      aimDist = this.lastAimDist;
      // So a switch to the classic scheme starts from where he is already facing.
      this.classic.face(this.lastAim);
    }

    let select = this.pendingSelect;
    this.pendingSelect = null;
    const steps = this.cycle + pad.cycle;
    this.cycle = 0;
    let fire = this.held('fire') || pad.fire;
    const tapped = this.knifeTapped || pad.knifePressed;
    this.knifeTapped = false;
    const script = this.knife.update(tapped, frame.current);
    if (script !== null) {
      select = script.select;
      fire = script.fire;
    } else if (select === null && steps !== 0) {
      select = cycleWeapon(frame.current, steps, frame.available);
    }

    const { run, crawl, sneak } = this.gaits.update(
      { held: this.held('run') || pad.run, pressed: this.runPressed || pad.runPressed },
      { held: this.held('crawl') || pad.crawl, pressed: this.crawlPressed || pad.crawlPressed },
      { held: this.held('sneak') || pad.sneak, pressed: this.sneakPressed || pad.sneakPressed },
      this.options,
    );
    this.runPressed = false;
    this.crawlPressed = false;
    this.sneakPressed = false;
    this.mapDown = this.held('map') || pad.map;

    return {
      moveX,
      moveZ,
      aim: this.lastAim,
      aimDist,
      run,
      crawl,
      sneak,
      fire,
      use,
      select,
      reload: this.held('reload') || pad.reload,
      firstAid: this.held('firstAid') || pad.firstAid,
      interact: this.held('interact') || pad.interact,
      recall: this.recall.update(this.held('recall') || pad.recall),
      fuse: this.fuse,
      aimAssist: classic ? CLASSIC_AIM_ASSIST : 0,
    };
  }
}
